import { html, nothing } from "lit";
import { formatRollingYearRange } from "../dateRange";
import { LmaxBaseChartComponent } from "../../shared/components/lmax-base-chart-component";

/**
 * The floating dark badge overlaid on a chart — a period ("Aug 25 - Aug 26"),
 * a label, and a big value with a chevron, e.g.
 * "Aug 25 - Aug 26 / LMAX Exchange uptime / ❯100%". Unlike `<lmax-chart-legend>`
 * this isn't slotted into the stats row: the parent chart routes it into a
 * `slot="range-legend"` positioned over the chart itself (see
 * `lmax-web-latency-chart`'s `.chart-wrap`).
 */
export class LmaxChartRangeLegend extends LmaxBaseChartComponent {
  static properties = {
    lableText: { type: String, attribute: "labletext" },
    value: { type: String, attribute: "value" },
    label: { attribute: false },
    styleHref: { type: String, attribute: "stylehref" },
  };

  declare lableText: string;
  declare value: string;
  /**
   * The period text, e.g. `"Aug 25 - Aug 26"`. Set by the parent chart from
   * the fetched dataset's own `label` field (`fetchLatencyDataFrom`) — not
   * an HTML attribute, since it's data-derived rather than authored on the
   * page. Falls back to a client-computed rolling 12-month range
   * (`formatRollingYearRange`) when the data source doesn't supply one, e.g.
   * the CSV fixture used in mock mode.
   */
  declare label: string | null;
  declare styleHref: string;

  constructor() {
    super();
    this.lableText = "";
    this.value = "";
    this.label = null;
    this.styleHref = "";
  }

  render() {
    if (!this.lableText) return nothing;
    return html`
      <link rel="stylesheet" href=${this.styleHref} />
      ${this.cssIncludesResolved}
      <div class="range-legend">
        <div class="range-legend-period">${this.label ?? formatRollingYearRange()}</div>
        <div class="range-legend-label">${this.lableText}</div>
        ${this.value
          ? html`
              <div class="range-legend-value">
                <span class="range-legend-arrow" aria-hidden="true">&#10095;</span>${this.value}
              </div>
            `
          : nothing}
      </div>
    `;
  }
}

customElements.define("lmax-chart-range-legend", LmaxChartRangeLegend);

declare global {
  interface HTMLElementTagNameMap {
    "lmax-chart-range-legend": LmaxChartRangeLegend;
  }
}
